import { getPrimaryStatementStatus } from "@/billing/components/StatementsTab/utils/statements";
import { OpenStatementsRow } from "@/billing/types/open-statements";
import {
  createTableSorter,
  getNextTableSortState,
  SortDirection,
  TableSortState,
} from "@/billing/utils/table-sort";

export type OpenStatementsSortColumnKey =
  | "client-name"
  | "service-date"
  | "charges"
  | "due-from"
  | "status"
  | "payments"
  | "adjustments"
  | "balance";

export type OpenStatementsSortDirection = SortDirection;

export type OpenStatementsTableSortState = TableSortState<OpenStatementsSortColumnKey>;

/** Initial Open Statements sort: chronological by statement date, oldest open statement first. */
export const DEFAULT_OPEN_STATEMENTS_TABLE_SORT_STATE: OpenStatementsTableSortState = {
  columnKey: "service-date",
  direction: "asc",
};

/**
 * Returns a new array of Open Statements rows sorted by column and direction. Rows with nothing in
 * the sorted column go last. Passing no sort state leaves the service's order untouched.
 *
 * Client rows tie-break on MRN so two clients sharing a name keep a stable order. Status rows sort
 * by the badge's visible label and tie-break on the underlying status, so the rolled-up `Accepted`
 * rows stay grouped and keep a stable order within the group.
 */
export const sortOpenStatementsTableRows = createTableSorter<
  OpenStatementsRow,
  OpenStatementsSortColumnKey
>({
  "client-name": [(row) => row.clientName, (row) => row.medicalRecordNumber],
  // `Invoice.date` is a FHIR date, so ISO-8601 ordering is chronological ordering.
  "service-date": (row) => row.serviceDate,
  charges: (row) => row.charges,
  "due-from": (row) => row.dueFrom,
  // The Open Statements badge is read-only, so it only ever shows the primary status: an insurance
  // statement on the accepted branch reads as `Accepted` with no sub-status badge beneath it.
  // Sorting on the raw status would scatter identical-looking `Accepted` badges across the column
  // and drop `Denied` rows in among them.
  status: [
    (row) => getPrimaryStatementStatus(row.status, row.servicePayerCode),
    (row) => row.status,
  ],
  payments: (row) => row.payments,
  adjustments: (row) => row.adjustments,
  balance: (row) => row.balance,
});

/**
 * Returns the next sort state after a column header click. The first click on a column sorts
 * ascending; clicking the same column again reverses direction.
 */
export function getNextOpenStatementsTableSortState(
  current: OpenStatementsTableSortState | undefined,
  columnKey: OpenStatementsSortColumnKey,
): OpenStatementsTableSortState {
  return getNextTableSortState(current, columnKey);
}
